import type { ReportPapEventFn } from "@mirage/analytics/AnalyticsProvider";
import type { AiLandingPageSuggestion } from "@mirage/analytics/events/enums/ai_landing_page_suggestion";
import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import type { DashExperience } from "@mirage/analytics/events/enums/dash_experience";
import type { DropboxAiVariant } from "@mirage/analytics/events/enums/dropbox_ai_variant";
import type { FeatureLine } from "@mirage/analytics/events/enums/feature_line";
import type { Prompt_1 } from "@mirage/analytics/events/enums/prompt_1";
import type { Prompt_2 } from "@mirage/analytics/events/enums/prompt_2";
import type { Prompt_3 } from "@mirage/analytics/events/enums/prompt_3";
import type { Select_DropboxAiAsk } from "@mirage/analytics/events/types/select_dropbox_ai_ask";
import { PAP_Select_DropboxAiAsk } from "@mirage/analytics/events/types/select_dropbox_ai_ask";
import type { Shown_DropboxAi } from "@mirage/analytics/events/types/shown_dropbox_ai";
import { PAP_Shown_DropboxAi } from "@mirage/analytics/events/types/shown_dropbox_ai";
import type { ConversationSource } from "@mirage/service-fss-bridge/service/bridge_types";
import { ACTION_SURFACE_MAPPING, DROPBOX_AI_BLADE } from "./constants";

/**
 * Maps isFirstTimeChat boolean to DropboxAiVariant enum
 */
export const getDropboxAiVariant = (
  isFirstTimeChat?: boolean,
): DropboxAiVariant => {
  return isFirstTimeChat
    ? "first_time_user_experience"
    : "new_chat_empty_state";
};

/**
 * Helper function to get surface-related properties for PAP events
 */
export const getSurfaceInfo = (source?: ConversationSource) => {
  // Get action_surface from ConversationSource mapping
  const actionSurface = source ? ACTION_SURFACE_MAPPING[source] : undefined;

  // Determine if root folder based on source
  const isRootFolder: boolean = source === "fss-unscoped";

  // dash_embedded for FSS
  const dashExperience: DashExperience = "dash_embedded";

  // Universal featureLine for assist functionality
  const featureLine: FeatureLine = "assist";

  return {
    actionElement: DROPBOX_AI_BLADE,
    actionSurface,
    isRootFolder,
    dashExperience,
    featureLine,
  };
};

/**
 * Helper function to log shown.dropbox_ai event
 */
export const logShownDropboxAiEvent = (
  reportPapEvent: ReportPapEventFn,
  params: {
    source?: ConversationSource;
    chatSessionId?: string;
    prompts?: (Prompt_1 | Prompt_2 | Prompt_3)[]; // Array of prompt enums (1-3 items)
    isFirstTimeChat?: boolean; // For dropboxAiVariant mapping
    dashActionSurface?: DashActionSurface; // From useDashActionSurface hook
  },
): void => {
  const {
    source,
    chatSessionId,
    prompts = [],
    isFirstTimeChat,
    dashActionSurface,
  } = params;
  const {
    actionElement,
    actionSurface,
    isRootFolder,
    dashExperience,
    featureLine,
  } = getSurfaceInfo(source);
  const dropboxAiVariant = getDropboxAiVariant(isFirstTimeChat);

  const eventProperties: Shown_DropboxAi["properties"] = {
    actionElement,
    actionSurface,
    chatSessionId,
    isRootFolder,
    dashExperience,
    dropboxAiVariant,
    dashActionSurface,
    featureLine,
    ...(prompts[0] ? { prompt_1: prompts[0] } : {}),
    ...(prompts[1] ? { prompt_2: prompts[1] } : {}),
    ...(prompts[2] ? { prompt_3: prompts[2] } : {}),
  };

  reportPapEvent(PAP_Shown_DropboxAi(eventProperties));
};

/**
 * Helper function to log select.dropbox_ai_ask event
 */
export const logSelectDropboxAiAskEvent = (
  reportPapEvent: ReportPapEventFn,
  params: {
    source?: ConversationSource;
    chatSessionId?: string;
    prompts?: (Prompt_1 | Prompt_2 | Prompt_3)[]; // Array of prompt enums shown (1-3 items)
    selectedPromptText?: AiLandingPageSuggestion; // The specific prompt enum that was clicked
    isFirstTimeChat?: boolean; // For dropboxAiVariant mapping
    dashActionSurface?: DashActionSurface; // From useDashActionSurface hook
  },
): void => {
  const {
    source,
    chatSessionId,
    prompts = [],
    selectedPromptText,
    isFirstTimeChat,
    dashActionSurface,
  } = params;
  const { actionElement, actionSurface, dashExperience, featureLine } =
    getSurfaceInfo(source);
  const dropboxAiVariant = getDropboxAiVariant(isFirstTimeChat);

  const eventProperties: Select_DropboxAiAsk["properties"] = {
    actionElement,
    actionSurface,
    chatSessionId,
    dashExperience,
    dropboxAiVariant,
    dashActionSurface,
    featureLine,
    ...(prompts[0] ? { prompt_1: prompts[0] } : {}),
    ...(prompts[1] ? { prompt_2: prompts[1] } : {}),
    ...(prompts[2] ? { prompt_3: prompts[2] } : {}),
    ...(selectedPromptText
      ? { aiLandingPageSuggestion: selectedPromptText }
      : {}),
  };

  reportPapEvent(PAP_Select_DropboxAiAsk(eventProperties));
};
